/**
 * SVG serialization (PLAN §2.5).
 *
 * Two modes, because "the SVG" means two different things to two audiences:
 *
 * - **Centerline** keeps one `<path>` per stroke with a `stroke-width`, so the
 *   document opens in Illustrator or Inkscape as editable strokes — the same
 *   objects the artist would have drawn. This is the default.
 * - **Outline** bakes the variable width into a filled boundary. It reproduces
 *   tapered strokes exactly but is no longer editable as a centerline.
 *
 * Coordinates are always in *working* resolution, and so is the `viewBox`; the
 * `width`/`height` attributes carry the *source* dimensions. The document
 * therefore renders at the original image size while every number inside it
 * still matches the pipeline's own coordinate system — which is what makes the
 * golden-file comparisons meaningful and keeps a downsampled conversion from
 * silently shrinking the artwork.
 *
 * Nothing here trusts its input to be well-formed: parameters flow into an XML
 * comment and stroke geometry can be degenerate, so both are sanitized rather
 * than interpolated raw.
 */

import type { Stroke, SvgOptions, VectorizationResult } from '$lib/types'
import { widthAt } from '$lib/types'
import {
  buildArcLengthTable,
  locateArcLength,
  normalAt,
  pointAt,
  segmentsIn,
  type ArcLocation,
} from '$lib/geometry/bezier'
import { fitCurve } from './fit'

/** Spacing, in pixels, at which a centerline is sampled before being offset. */
const OUTLINE_SAMPLE_SPACING = 1.5

/** Error budget when refitting an offset polyline back to cubics. */
const OUTLINE_FIT_ERROR = 0.2

/** Guards against a zero-width stroke rendering as nothing at all. */
const MIN_STROKE_WIDTH = 0.1

/** Golden angle, in degrees: the hue step that keeps adjacent indices distinct. */
const GOLDEN_ANGLE = 137.508

// ---------------------------------------------------------------------------
// Path data
// ---------------------------------------------------------------------------

/** `M`/`C` path data for a stroke's centerline. Emits `Z` for closed strokes. */
export function centerlinePathData(stroke: Stroke, precision: number): string {
  const segments = segmentsIn(stroke.points)
  if (!Number.isInteger(segments) || segments < 1) return ''
  const p = stroke.points
  const parts: string[] = ['M', num(p[0], precision), num(p[1], precision)]
  for (let s = 0; s < segments; s++) {
    const i = s * 6
    parts.push(
      'C',
      num(p[i + 2], precision),
      num(p[i + 3], precision),
      num(p[i + 4], precision),
      num(p[i + 5], precision),
      num(p[i + 6], precision),
      num(p[i + 7], precision),
    )
  }
  if (stroke.closed) parts.push('Z')
  return parts.join(' ')
}

/**
 * Closed outline of a variable-width stroke: offsets the centerline by ±w(T)
 * along the normal, refits both sides to Béziers, and joins them with round caps.
 *
 * The offset of a cubic is not itself a cubic, so the two sides are sampled and
 * refitted rather than derived analytically — the same fitter the centerlines
 * came from, at a much tighter error budget.
 */
export function outlinePathData(stroke: Stroke, precision: number): string {
  const segments = segmentsIn(stroke.points)
  if (!Number.isInteger(segments) || segments < 1) return ''

  const table = buildArcLengthTable(stroke.points)
  if (!(table.total > 1e-6)) {
    // A zero-length stroke is a dot: emit a disc rather than an empty path.
    return dotPathData(stroke.points[0], stroke.points[1], halfWidth(stroke, 0), precision)
  }

  const count = Math.max(8, Math.ceil(table.total / OUTLINE_SAMPLE_SPACING) + 1)
  const left = new Float64Array(count * 2)
  const right = new Float64Array(count * 2)
  const location: ArcLocation = { seg: 0, u: 0 }
  const point = { x: 0, y: 0 }
  let nx = 0
  let ny = 0
  for (let i = 0; i < count; i++) {
    const t = i / (count - 1)
    locateArcLength(table, t * table.total, location)
    pointAt(stroke.points, location.seg, location.u, point)
    const normal = normalAt(stroke.points, location.seg, location.u)
    // A cusp has no defined normal; carrying the previous one forward keeps the
    // offset continuous instead of collapsing it onto the centerline.
    if (normal.x !== 0 || normal.y !== 0) {
      nx = normal.x
      ny = normal.y
    }
    const w = halfWidth(stroke, t)
    left[i * 2] = point.x + nx * w
    left[i * 2 + 1] = point.y + ny * w
    right[i * 2] = point.x - nx * w
    right[i * 2 + 1] = point.y - ny * w
  }

  if (stroke.closed) {
    // Two rings with opposite winding, so the non-zero fill rule punches the
    // interior out instead of filling it.
    const outer = curveData(fitCurve(left, OUTLINE_FIT_ERROR, true), precision, true)
    const inner = curveData(
      fitCurve(reversePolyline(right), OUTLINE_FIT_ERROR, true),
      precision,
      true,
    )
    const rings: string[] = []
    if (outer) rings.push(`${outer} Z`)
    if (inner) rings.push(`${inner} Z`)
    return rings.join(' ')
  }

  const leftCurve = fitCurve(left, OUTLINE_FIT_ERROR, false)
  const rightCurve = fitCurve(reversePolyline(right), OUTLINE_FIT_ERROR, false)
  if (leftCurve.length < 8 || rightCurve.length < 8) return ''

  const parts: string[] = [curveData(leftCurve, precision, true)]
  parts.push(
    cap(
      halfWidth(stroke, 1),
      right[(count - 1) * 2],
      right[(count - 1) * 2 + 1],
      precision,
    ),
  )
  parts.push(curveData(rightCurve, precision, false))
  parts.push(cap(halfWidth(stroke, 0), left[0], left[1], precision))
  parts.push('Z')
  return parts.join(' ')
}

/**
 * Round cap: a half-circle of radius `w` ending at `(x, y)`.
 *
 * `sweep-flag = 0` because the left offset is `P + w·n` with `n` the left
 * normal `(-dy, dx)`; going left-offset → tip → right-offset walks the circle
 * in the direction of *decreasing* angle in SVG's y-down frame.
 */
function cap(w: number, x: number, y: number, precision: number): string {
  if (!(w > 1e-6)) return `L ${num(x, precision)} ${num(y, precision)}`
  const r = num(w, precision)
  return `A ${r} ${r} 0 0 0 ${num(x, precision)} ${num(y, precision)}`
}

function dotPathData(cx: number, cy: number, w: number, precision: number): string {
  const r = Math.max(w, MIN_STROKE_WIDTH / 2)
  const rr = num(r, precision)
  const leftX = num(cx - r, precision)
  const rightX = num(cx + r, precision)
  const y = num(cy, precision)
  return `M ${leftX} ${y} A ${rr} ${rr} 0 1 0 ${rightX} ${y} A ${rr} ${rr} 0 1 0 ${leftX} ${y} Z`
}

/** `C` commands for a control-point array, optionally prefixed with its `M`. */
function curveData(points: Float32Array, precision: number, withMove: boolean): string {
  const segments = segmentsIn(points)
  if (!Number.isInteger(segments) || segments < 1) return ''
  const parts: string[] = []
  if (withMove) parts.push('M', num(points[0], precision), num(points[1], precision))
  for (let s = 0; s < segments; s++) {
    const i = s * 6
    parts.push(
      'C',
      num(points[i + 2], precision),
      num(points[i + 3], precision),
      num(points[i + 4], precision),
      num(points[i + 5], precision),
      num(points[i + 6], precision),
      num(points[i + 7], precision),
    )
  }
  return parts.join(' ')
}

function reversePolyline(points: Float64Array): Float64Array {
  const count = points.length / 2
  const out = new Float64Array(points.length)
  for (let i = 0; i < count; i++) {
    out[i * 2] = points[(count - 1 - i) * 2]
    out[i * 2 + 1] = points[(count - 1 - i) * 2 + 1]
  }
  return out
}

function halfWidth(stroke: Stroke, t: number): number {
  const w = widthAt(stroke.width, t)
  return Number.isFinite(w) && w > 0 ? w : 0
}

/**
 * Mean half-width over normalized arc length: ∫₀¹ Σ a_k T^k dT = Σ a_k/(k+1).
 * Exact, so the centerline `stroke-width` does not depend on a sample count.
 */
export function meanHalfWidth(stroke: Stroke): number {
  let mean = 0
  for (let k = 0; k < stroke.width.length; k++) mean += stroke.width[k] / (k + 1)
  return Number.isFinite(mean) ? mean : 0
}

// ---------------------------------------------------------------------------
// Colour
// ---------------------------------------------------------------------------

/**
 * Distinct hue per stroke index by golden-angle rotation — deterministic, so
 * the colorized document is as reproducible as the plain one, and free of the
 * clustering a naive `index / count` hue ramp produces.
 *
 * Emitted as hex rather than `hsl()` because not every SVG consumer resolves
 * CSS colour functions.
 */
export function strokeColor(index: number): string {
  const hue = (((index * GOLDEN_ANGLE) % 360) + 360) % 360
  return hslToHex(hue, 0.68, 0.45)
}

function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const hp = h / 60
  const x = c * (1 - Math.abs((hp % 2) - 1))
  let r = 0
  let g = 0
  let b = 0
  if (hp < 1) [r, g, b] = [c, x, 0]
  else if (hp < 2) [r, g, b] = [x, c, 0]
  else if (hp < 3) [r, g, b] = [0, c, x]
  else if (hp < 4) [r, g, b] = [0, x, c]
  else if (hp < 5) [r, g, b] = [x, 0, c]
  else [r, g, b] = [c, 0, x]
  const m = l - c / 2
  const channel = (v: number): string =>
    Math.round(Math.min(255, Math.max(0, (v + m) * 255)))
      .toString(16)
      .padStart(2, '0')
  return `#${channel(r)}${channel(g)}${channel(b)}`
}

// ---------------------------------------------------------------------------
// Document
// ---------------------------------------------------------------------------

/** Serializes a result to a standalone SVG document. */
export function toSvg(result: VectorizationResult, options: SvgOptions): string {
  const precision = clampPrecision(options.precision)
  const lines: string[] = ['<?xml version="1.0" encoding="UTF-8"?>']
  lines.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${attr(num(result.sourceWidth, 4))}"` +
      ` height="${attr(num(result.sourceHeight, 4))}"` +
      ` viewBox="0 0 ${attr(num(result.width, 4))} ${attr(num(result.height, 4))}">`,
  )
  if (options.includeMetadata) lines.push(`  ${xmlComment(metadataText(result, options))}`)

  for (let i = 0; i < result.strokes.length; i++) {
    const stroke = result.strokes[i]
    const color = options.colorize ? strokeColor(i) : '#000000'
    if (options.mode === 'outline') {
      const data = outlinePathData(stroke, precision)
      if (!data) continue
      lines.push(`  <path d="${attr(data)}" fill="${attr(color)}" stroke="none"/>`)
    } else {
      const data = centerlinePathData(stroke, precision)
      if (!data) continue
      const width = Math.max(MIN_STROKE_WIDTH, 2 * meanHalfWidth(stroke))
      lines.push(
        `  <path d="${attr(data)}" fill="none" stroke="${attr(color)}"` +
          ` stroke-width="${attr(num(width, precision))}"` +
          ` stroke-linecap="round" stroke-linejoin="round"/>`,
      )
    }
  }

  lines.push('</svg>')
  return `${lines.join('\n')}\n`
}

function metadataText(result: VectorizationResult, options: SvgOptions): string {
  const summary = [
    'line-art-vectoring',
    `mode=${options.mode}`,
    `strokes=${result.strokes.length}`,
    `working=${num(result.width, 4)}x${num(result.height, 4)}`,
    `source=${num(result.sourceWidth, 4)}x${num(result.sourceHeight, 4)}`,
    `iterations=${num(result.iterations, 4)}`,
    `loss=${num(result.loss, 6)}`,
  ].join(' ')
  return `${summary} params=${JSON.stringify(result.params)}`
}

/**
 * Wraps text in an XML comment. `--` is illegal inside a comment and a trailing
 * `-` would run into the terminator, so both are neutralized; control
 * characters are dropped because they are not valid XML characters at all.
 */
function xmlComment(text: string): string {
  const safe = text
    .replace(/[\u0000-\u001f]/g, ' ')
    .replace(/-{2,}/g, '-')
    .replace(/-$/, '- ')
  return `<!-- ${safe} -->`
}

function attr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

function clampPrecision(precision: number): number {
  if (!Number.isFinite(precision)) return 2
  return Math.min(12, Math.max(0, Math.floor(precision)))
}

/** Fixed-precision number without trailing zeros, and without `-0`. */
function num(value: number, precision: number): string {
  if (!Number.isFinite(value)) return '0'
  const rounded = Number(value.toFixed(clampPrecision(precision)))
  if (rounded === 0) return '0'
  return String(rounded)
}
